import {
  getDPIThreshold,
  MATERIAL_TYPES,
  type TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import {
  getTileCropParams,
  getTileSize,
} from '../../components/PhotoBank/tileUtils'
import { getCropTargetAreaInches } from '../../utils/cropRatioUtils'
import { getTileStyleData } from '../tilesStyling'
import { type Tile } from '../../components/TilesProvider/TilesProvider.types'
import minBy from 'lodash/minBy'
import sumBy from 'lodash/sumBy'

export type FaceMetrics = {
  area: number
  widthPixels: number
  heightPixels: number
  relativeArea: number // face area as ratio of tile (0-1)
}

export type Face = {
  width: number
  height: number
  belowCropThreshold?: boolean
}

export function getTileDPI(tile: Tile): number | null {
  const cropParams = getTileCropParams(tile)
  const imageWidth = cropParams?.width || tile.width
  const imageHeight = cropParams?.height || tile.height

  if (!imageWidth || !imageHeight) {
    return null
  }

  const tileSize = getTileSize(tile) as TILE_SIZES
  const materialType = tile.materialType || MATERIAL_TYPES.CLASSIC
  const { matting } = getTileStyleData({
    customStyle: tile.customStyle,
    materialType,
  })

  const cropTargetArea = getCropTargetAreaInches({
    mattingType: matting,
    materialType,
    tileSize,
  })

  if (!cropTargetArea) {
    return null
  }

  return Math.round(
    Math.min(
      imageWidth / cropTargetArea.width,
      imageHeight / cropTargetArea.height
    )
  )
}

function computeSingleFaceMetrics(
  face: Face,
  imageWidth: number,
  imageHeight: number
): FaceMetrics {
  const widthPixels = Math.round(face.width * imageWidth)
  const heightPixels = Math.round(face.height * imageHeight)
  return {
    area: widthPixels * heightPixels,
    widthPixels,
    heightPixels,
    relativeArea: face.width * face.height, // already 0-1 ratios
  }
}

export function getSmallestFaceMetrics(tile: {
  faces?: Face[]
  width: number
  height: number
}): FaceMetrics | null {
  const { faces, width, height } = tile
  if (!faces || faces.length === 0) {
    return null
  }
  return (
    minBy(
      faces.map((face) => computeSingleFaceMetrics(face, width, height)),
      'area'
    ) ?? null
  )
}

export function getFaceMetrics(data: {
  faces?: Face[]
  faceDetectionSuccess?: boolean | null
  width: number
  height: number
  smallFacesFiltered?: number
}) {
  const smallest = getSmallestFaceMetrics(data)
  const belowCropThreshold = data.faces?.filter((f) => f.belowCropThreshold)
    .length
  return {
    'Face Count': data.faces?.length,
    'Face Detection Success': data.faceDetectionSuccess,
    ...(belowCropThreshold && {
      'Faces Below Crop Threshold': belowCropThreshold,
    }),
    ...(smallest && {
      'Smallest Face Width Pixels': smallest.widthPixels,
      'Smallest Face Height Pixels': smallest.heightPixels,
      'Smallest Face Area Pixels': smallest.area,
      'Smallest Face Relative Size': smallest.relativeArea,
    }),
    ...(data.smallFacesFiltered != null && {
      'Small Faces Filtered': data.smallFacesFiltered,
    }),
  }
}

export function getTileFaceMetrics(tile: Tile & { faces?: Face[] }) {
  if (!tile.width || !tile.height) {
    return {
      'Face Count': tile.faces?.length,
      'Face Detection Success': tile.faceDetectionSuccess,
    }
  }

  const faceMetrics = getFaceMetrics(tile)
  const smallest = getSmallestFaceMetrics(tile)

  try {
    const cropParams = getTileCropParams(tile)
    const cropArea =
      cropParams?.width && cropParams?.height
        ? cropParams.width * cropParams.height
        : null
    const faceAreaRelativeToCrop =
      smallest?.area && cropArea ? smallest.area / cropArea : null

    return {
      ...faceMetrics,
      'Megapixels (MP)': (tile.width * tile.height) / 1_000_000,
      DPI: getTileDPI(tile),
      ...(faceAreaRelativeToCrop !== null && {
        'Smallest Face Area Relative To Crop': faceAreaRelativeToCrop,
      }),
    }
  } catch {
    return faceMetrics
  }
}

export function isFixableByUpscale(tile: Tile, scale: number): boolean | null {
  const dpi = getTileDPI(tile)
  const threshold = getDPIThreshold(tile.tileSize)
  if (!dpi) {
    return null
  }
  return dpi * scale >= threshold
}

export function getUpscaledMegapixels(tile: Tile): number {
  const dpi = getTileDPI(tile)!
  const threshold = getDPIThreshold(tile.tileSize)
  const scaleFactor = dpi * 2 >= threshold ? 2 : 3

  const cropParams = getTileCropParams(tile)
  const cropWidth = cropParams?.width || tile.width
  const cropHeight = cropParams?.height || tile.height

  return (cropWidth * scaleFactor * cropHeight * scaleFactor) / 1_000_000
}

export function getOrderFaceAndDPIMetrics(
  tiles: (Tile & { faces?: Face[] })[]
) {
  if (!tiles || tiles.length === 0) {
    return {}
  }

  const tilesWithFaces = tiles.filter(
    (tile) => tile.faces && tile.faces.length > 0
  )
  const totalFaceCount = sumBy(
    tilesWithFaces,
    (tile) => tile.faces?.length ?? 0
  )
  const faceDetectionFailedCount = tiles.filter(
    (tile) => tile.faceDetectionSuccess === false
  ).length
  const totalBelowCropThreshold = sumBy(
    tilesWithFaces,
    (tile) => tile.faces?.filter((f) => f.belowCropThreshold).length ?? 0
  )

  const smallestFaces = tilesWithFaces
    .map((tile) => getSmallestFaceMetrics(tile))
    .filter(Boolean) as FaceMetrics[]
  const smallestFace = minBy(smallestFaces, 'area')

  const dpiValues = tiles
    .map(getTileDPI)
    .filter((dpi): dpi is number => dpi !== null)

  return {
    'Total Face Count': totalFaceCount,
    'Tiles With Faces Count': tilesWithFaces.length,
    'Face Detection Failed Count': faceDetectionFailedCount,
    'Total Faces Below Crop Threshold': totalBelowCropThreshold,
    ...(smallestFace && {
      'Smallest Face Area Pixels (Order)': smallestFace.area,
      'Smallest Face Relative Size (Order)': smallestFace.relativeArea,
    }),
    ...(dpiValues.length > 0 && {
      'Average DPI': Math.round(sumBy(dpiValues) / dpiValues.length),
      'Minimum DPI': Math.round(Math.min(...dpiValues)),
    }),
  }
}
